/**
 * How much of a crowd-control effect a monster takes, by its level.
 *
 * An archetype's `ccSusceptibility` is its susceptibility at the top level
 * (elite 0.5, boss 0.15 — the values that were once applied at every level);
 * a monster below the top takes more of it, down to full susceptibility at
 * level 1. The runtime reads it here for every crowd-control status it applies.
 *
 * PROVISIONAL: the straight line between level 1 and `MAX_ENEMY_LEVEL` is a
 * measuring instrument for "resistance rises with monster level up to today's
 * values" (10/13 CC rule), not a calibrated curve. Change it here and nowhere
 * else.
 */
import { MAX_ENEMY_LEVEL } from './enemy-level.js';

/** The susceptibility of a monster of `level` whose archetype's susceptibility at the top level is `topSusceptibility`. */
export function ccSusceptibilityAtLevel(topSusceptibility: number, level: number): number {
  const progress = (level - 1) / (MAX_ENEMY_LEVEL - 1);
  return 1 - (1 - topSusceptibility) * progress;
}

/**
 * The one target a skill is priced against, since it is priced before its
 * target is known: halfway between a monster that takes all of a crowd-control
 * effect and a resistant one (a level-60 elite takes 0.70 of a status and 0.5
 * of a displacement). The appraisal's context and the authoring that inverts it
 * both read these numbers; the runtime applies each monster's own
 * `ccSusceptibilityAtLevel` / displacement resistance.
 *
 * PROVISIONAL: a measuring instrument, not a calibrated balance decision.
 * Change it here and nowhere else.
 */
export const CC_PRICING_REFERENCE = {
  /** The share of a status's duration or slow the reference target takes. */
  ccSusceptibility: 0.85,
  /** The share of a displacement the reference target takes. */
  displacementSusceptibility: 0.75,
} as const;

/** The susceptibility a crowd-control status is priced at. */
export const CC_PRICING_SUSCEPTIBILITY = CC_PRICING_REFERENCE.ccSusceptibility;

/** The share of a displacement the reference target takes. */
export const DISPLACEMENT_PRICING_SUSCEPTIBILITY = CC_PRICING_REFERENCE.displacementSusceptibility;
